/**
 * The path a Plinko ball draws, as one continuous curve.
 *
 * Units: x and y are in peg gaps. x is relative to the board's centre line, y is relative to
 * the apex peg row (row r sits at y = r). Time is in "steps": one step is the scene's
 * per-row duration, so a drop over `rows` rows lasts exactly `rows` steps whatever speed
 * (turbo, reduced motion) the scene runs at.
 *
 * The ball passes the same gaps the server's path names: after its k-th deflection it is
 * at row k-1's height, half a column per deflection to the side. Between those waypoints
 * it follows a cubic Hermite curve whose velocity is shared at every waypoint, so speed
 * never resets at a row: the descent quickens row by row like a falling ball, and dips
 * around each peg it glances off (the bounce), then picks up again.
 */

export type PlinkoDir = 'L' | 'R';

export interface PlinkoTrajectory {
  /** Waypoint k: x / y in gaps, and the velocity there in gaps per step. */
  readonly x: Float64Array;
  readonly y: Float64Array;
  readonly vx: Float64Array;
  readonly vy: Float64Array;
  /** Start of segment k (waypoint k → k+1), in steps; `start[rows] === rows`. */
  readonly start: Float64Array;
  /** Signed column (in half gaps) the ball enters each segment from. */
  readonly col: Int32Array;
  readonly rows: number;
}

/** Peak-to-mean vertical speed at a waypoint: above 1 the ball slows around each peg (the bounce). */
const BOUNCE = 1.4;
/** Share of a segment's sideways speed the ball still carries as it passes a gap. */
const CARRY = 0.55;
/** The first segment is a drop from rest, so it takes longer than a row hop. */
const SPAWN_WEIGHT = 1.3;
/** How much shorter the last rows are than the first, as the ball gathers speed. */
const SPEED_UP = 0.28;
const SPEED_UP_ROWS = 3;
/** Where in a segment the ball glances off the peg under it (its slowest point). */
export const CONTACT_AT = 0.5;

/**
 * @param exitSpeed vertical speed (gaps per step) at the last waypoint — the settle animation's
 *   opening speed, so the hand-off into the cup carries on without a jolt.
 */
export function buildTrajectory(path: readonly PlinkoDir[], exitSpeed: number): PlinkoTrajectory {
  const rows = path.length;
  const n = rows + 1;
  const x = new Float64Array(n);
  const y = new Float64Array(n);
  const vx = new Float64Array(n);
  const vy = new Float64Array(n);
  const start = new Float64Array(n);
  const col = new Int32Array(n);

  const dur = new Float64Array(rows);
  let total = 0;
  for (let k = 0; k < rows; k++) {
    dur[k] = k === 0 ? SPAWN_WEIGHT : 1 - SPEED_UP * (1 - Math.exp(-(k - 1) / SPEED_UP_ROWS));
    total += dur[k]!;
  }
  const scale = rows > 0 ? rows / total : 0;
  for (let k = 0; k < rows; k++) dur[k]! *= scale;

  for (let k = 0; k < n; k++) {
    if (k > 0) {
      col[k] = col[k - 1]! + (path[k - 1] === 'R' ? 1 : -1);
      start[k] = start[k - 1]! + dur[k - 1]!;
    }
    x[k] = col[k]! / 2;
    y[k] = k - 1;
  }
  if (rows > 0) start[rows] = rows;

  for (let k = 1; k < rows; k++) {
    const meanDur = (dur[k - 1]! + dur[k]!) / 2;
    vy[k] = BOUNCE / meanDur;
    vx[k] = (CARRY * (path[k - 1] === 'R' ? 0.5 : -0.5)) / meanDur;
  }
  // Spawn: dropped from rest. Exit: straight down into the settle, which only moves vertically.
  vy[rows] = rows > 0 ? exitSpeed : 0;

  return { x, y, vx, vy, start, col, rows };
}

/** Index of the segment `progress` (in steps) falls in, clamped to the last one. */
export function segmentAt(traj: PlinkoTrajectory, progress: number): number {
  const last = Math.max(0, traj.rows - 1);
  let k = 0;
  while (k < last && progress >= traj.start[k + 1]!) k++;
  return k;
}

/**
 * Position at `progress` steps into the drop, written into `out` (no allocation: the scene
 * samples every ball every frame).
 */
export function sampleTrajectory(
  traj: PlinkoTrajectory,
  progress: number,
  out: { x: number; y: number },
  hint = 0,
): number {
  if (traj.rows === 0) {
    out.x = 0;
    out.y = -1;
    return 0;
  }
  const last = traj.rows - 1;
  let k = Math.min(Math.max(0, hint), last);
  while (k > 0 && progress < traj.start[k]!) k--;
  while (k < last && progress >= traj.start[k + 1]!) k++;
  const t0 = traj.start[k]!;
  const d = traj.start[k + 1]! - t0;
  const p = Math.min(1, Math.max(0, (progress - t0) / d));
  const p2 = p * p;
  const p3 = p2 * p;
  const h00 = 2 * p3 - 3 * p2 + 1;
  const h10 = p3 - 2 * p2 + p;
  const h01 = -2 * p3 + 3 * p2;
  const h11 = p3 - p2;
  out.x = h00 * traj.x[k]! + h10 * traj.vx[k]! * d + h01 * traj.x[k + 1]! + h11 * traj.vx[k + 1]! * d;
  out.y = h00 * traj.y[k]! + h10 * traj.vy[k]! * d + h01 * traj.y[k + 1]! + h11 * traj.vy[k + 1]! * d;
  return k;
}

/** The peg segment k glances off: row k, directly under the gap the segment starts from. */
export function contactPeg(traj: PlinkoTrajectory, k: number): { row: number; col: number } {
  return { row: k, col: (k + traj.col[k]!) / 2 };
}

/** When (in steps) segment k reaches its peg. */
export function contactTime(traj: PlinkoTrajectory, k: number): number {
  const t0 = traj.start[k]!;
  return t0 + (traj.start[k + 1]! - t0) * CONTACT_AT;
}
